import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import UndoIcon from "@mui/icons-material/Undo";
import { fieldSx } from "../constants";

const VariantsPricingModal = ({
  open,
  onClose,
  variants,
  onVariantChange,
  onVariantsChange,
  productSkus = [],
}) => {
  const [selectedSkus, setSelectedSkus] = useState(new Set());
  const [removedVariants, setRemovedVariants] = useState([]);

  useEffect(() => {
    if (open) {
      setSelectedSkus(new Set());
      setRemovedVariants([]);
    }
  }, [open]);

  const allSelected =
    variants.length > 0 && selectedSkus.size === variants.length;
  const someSelected = selectedSkus.size > 0;

  const handleToggleSku = (index) => {
    setSelectedSkus((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  const handleSelectAllSkus = () => {
    if (allSelected) {
      setSelectedSkus(new Set());
    } else {
      setSelectedSkus(new Set(variants.map((_, i) => i)));
    }
  };

  const deleteByIndices = (indices) => {
    const idxSet = new Set(indices);
    const removed = variants.filter((_, i) => idxSet.has(i));
    const remaining = variants.filter((_, i) => !idxSet.has(i));
    setRemovedVariants((prev) => [...prev, ...removed]);
    setSelectedSkus(new Set());
    onVariantsChange(remaining);
  };

  const handleDeleteSelected = () => deleteByIndices([...selectedSkus]);
  const handleDeleteRow = (index) => deleteByIndices([index]);

  const handleRestoreVariant = (removedIndex) => {
    const toRestore = removedVariants[removedIndex];
    onVariantsChange([...variants, toRestore]);
    setRemovedVariants((prev) => prev.filter((_, i) => i !== removedIndex));
  };

  const headerCellSx = {
    backgroundColor: "var(--table-header-bg)",
    color: "var(--text-tertiary)",
    fontSize: "12px",
    fontWeight: 600,
    fontFamily: "Poppins",
    py: 0.75,
    px: 2,
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{ fontFamily: "Poppins", fontWeight: 600, fontSize: "15px", pb: 1 }}
      >
        Edit Variant Prices
      </DialogTitle>
      <Divider sx={{ borderColor: "var(--border-color)" }} />

      {/* Toolbar: select-all + bulk delete */}
      <Box
        sx={{
          px: 2,
          py: 0.5,
          display: "flex",
          alignItems: "center",
          gap: 1,
          borderBottom: "1px solid var(--border-color)",
        }}
      >
        <Checkbox
          size="small"
          checked={allSelected}
          indeterminate={someSelected && !allSelected}
          onChange={handleSelectAllSkus}
          disabled={variants.length === 0}
        />
        <Typography
          fontSize={13}
          fontFamily="Poppins"
          color="var(--text-secondary)"
          flex={1}
        >
          {someSelected
            ? `${selectedSkus.size} selected`
            : `${variants.length} SKU${variants.length !== 1 ? "s" : ""}`}
        </Typography>
        {someSelected && (
          <Tooltip title="Delete selected SKUs">
            <IconButton
              size="small"
              onClick={handleDeleteSelected}
              sx={{ color: "error.main" }}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <DialogContent sx={{ p: 0 }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...headerCellSx, width: 40, px: 1 }} />
                {["SKU", "Price", ""].map((h, i) => (
                  <TableCell
                    key={i}
                    sx={{
                      ...headerCellSx,
                      ...(h === "" ? { width: 48, px: 1 } : {}),
                    }}
                  >
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {variants.map((v, vi) => (
                <TableRow
                  key={vi}
                  sx={{
                    "&:nth-of-type(odd)": {
                      backgroundColor: "var(--row-bg-odd)",
                    },
                    "&:nth-of-type(even)": {
                      backgroundColor: "var(--row-bg-even)",
                    },
                  }}
                >
                  <TableCell
                    sx={{ px: 1, py: 0.5, width: 40, textAlign: "center" }}
                  >
                    <Checkbox
                      size="small"
                      checked={selectedSkus.has(vi)}
                      onChange={() => handleToggleSku(vi)}
                    />
                  </TableCell>
                  <TableCell sx={{ px: 2, py: 1 }}>
                    <TextField
                      size="small"
                      fullWidth
                      disabled
                      value={v.sku || ""}
                      sx={fieldSx}
                      inputProps={{ style: { fontSize: 12 } }}
                    />
                  </TableCell>
                  <TableCell sx={{ px: 2, py: 1 }}>
                    <TextField
                      size="small"
                      fullWidth
                      type="number"
                      value={v.price ?? ""}
                      onChange={(e) =>
                        onVariantChange(vi, "price", e.target.value)
                      }
                      sx={fieldSx}
                      inputProps={{
                        min: 0,
                        step: "0.01",
                        style: { fontSize: 12 },
                      }}
                    />
                  </TableCell>
                  <TableCell
                    sx={{ px: 1, py: 0.5, width: 48, textAlign: "center" }}
                  >
                    <Tooltip title="Remove SKU">
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteRow(vi)}
                        sx={{ color: "error.main", p: 0.5 }}
                      >
                        <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Removed variants — restore section */}
        {removedVariants.length > 0 && (
          <Box
            sx={{
              px: 2,
              py: 1.5,
              borderTop: "1px solid var(--border-color)",
            }}
          >
            <Typography
              fontSize={12}
              fontFamily="Poppins"
              color="var(--text-secondary)"
              fontWeight={500}
              sx={{ mb: 1 }}
            >
              Removed SKUs — click to restore
            </Typography>
            {removedVariants.map((rv, ri) => (
              <Box
                key={ri}
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}
              >
                <Typography
                  fontSize={12}
                  fontFamily="Poppins"
                  color="var(--text-tertiary)"
                  flex={1}
                >
                  {rv.sku}
                </Typography>
                <Tooltip title="Restore">
                  <IconButton
                    size="small"
                    onClick={() => handleRestoreVariant(ri)}
                    sx={{ color: "primary.main", p: 0.5 }}
                  >
                    <UndoIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            ))}
          </Box>
        )}

        {/* Product SKU suggestions — SKUs not yet added to the list */}
        {(() => {
          const activeSkuSet = new Set(variants.map((v) => v.sku));
          const removedSkuSet = new Set(removedVariants.map((v) => v.sku));
          const suggestions = productSkus.filter(
            (ps) => !activeSkuSet.has(ps.sku) && !removedSkuSet.has(ps.sku),
          );
          if (!suggestions.length) return null;
          return (
            <Box
              sx={{
                px: 2,
                py: 1.5,
                borderTop: "1px solid var(--border-color)",
              }}
            >
              <Typography
                fontSize={12}
                fontFamily="Poppins"
                color="var(--text-secondary)"
                fontWeight={500}
                sx={{ mb: 1 }}
              >
                Suggestions — click to add
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                {suggestions.map((ps, si) => (
                  <Box
                    key={si}
                    onClick={() =>
                      onVariantsChange([
                        ...variants,
                        {
                          sku: ps.sku,
                          price:
                            ps.price !== "" && ps.price != null ? ps.price : "",
                          _suggestedSku: ps.sku,
                          _suggestedPrice: ps.price ?? null,
                        },
                      ])
                    }
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.5,
                      px: 1.25,
                      py: 0.5,
                      borderRadius: "12px",
                      border: "1px dashed var(--border-color)",
                      cursor: "pointer",
                      backgroundColor: "var(--bg-secondary, var(--bg-paper))",
                      transition: "all 0.15s",
                      "&:hover": {
                        borderColor: "primary.main",
                        backgroundColor: "rgba(25,118,210,0.06)",
                      },
                    }}
                  >
                    <AddIcon
                      sx={{ fontSize: 11, color: "var(--text-tertiary)" }}
                    />
                    <Typography
                      fontSize={11}
                      fontFamily="Poppins"
                      color="var(--text-secondary)"
                    >
                      {ps.sku}
                      {ps.price !== "" && ps.price != null
                        ? ` · $${Number(ps.price).toFixed(2)}`
                        : ""}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          );
        })()}
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1.5 }}>
        <Button
          variant="contained"
          size="small"
          onClick={onClose}
          sx={{ textTransform: "none", fontFamily: "Poppins" }}
        >
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VariantsPricingModal;
